import * as React from 'react';

import type {
    GetQuoteById_quote_subtotalLineItems_edges_node as QuoteSubtotalLineItemType,
    GetQuoteById_quote_subtotal as Money,
    GetQuoteById_quote_total as TotalMoney,
} from 'nutshell-graphql-types';

import {Button} from '../../button/button';
import {DraggableList} from '../../draggable-row/draggable-list';
import {Body} from '../../typography/body';

import {calculateTotal} from '../helpers';

import {QuoteSubtotalRow} from './quote-subtotal-row';

import styles from '../quote.module.css';

type Props = {
    subtotal: Money;
    total: TotalMoney;
    subtotalLineItems: QuoteSubtotalLineItemType[];
    onUpdateLineItem: (arg1: QuoteSubtotalLineItemType) => void;
    onDeleteRow: (arg1: string) => void;
    onAddRow: () => void;
    onReorder: (arg1: number, arg2: number) => void;
};

export function QuoteSubtotalTable(props: Props) {
    const total = React.useMemo(() => {
        const subtotal = props.subtotal.amount;
        const totalCalculated = calculateTotal(props.subtotalLineItems, subtotal);

        return `${totalCalculated < 0 ? '-' : ''}${props.total.symbol}${Math.abs(
            totalCalculated
        ).toFixed(2)}`;
    }, [props.subtotalLineItems, props.subtotal, props.total.symbol]);

    const rows = React.useMemo(
        () =>
            props.subtotalLineItems.map((item) => ({
                id: item.id,
                component: (
                    <QuoteSubtotalRow
                        key={item.id}
                        subtotalLineItem={item}
                        subtotal={props.subtotal ? props.subtotal.amount : 0}
                        onDeleteRow={props.onDeleteRow}
                        onUpdateLineItem={props.onUpdateLineItem}
                    />
                ),
            })),
        [props.subtotal, props.onDeleteRow, props.subtotalLineItems, props.onUpdateLineItem]
    );

    return (
        <>
            <div className='full-width full-height br-3'>
                <div className={styles['subtotal-container']}>
                    <div className='flex justify-sb'>
                        <Body>Subtotal</Body>
                        <Body isBold={true}>{`${
                            props.subtotal.symbol
                        }${props.subtotal.amount.toFixed(2)}`}</Body>
                    </div>
                    {props.subtotalLineItems.length > 0 && (
                        <DraggableList
                            listItems={rows}
                            isInModal={true}
                            handleReorder={props.onReorder}
                            showOverflow={true}
                        />
                    )}
                    <div className={styles['add-row-button']}>
                        <Button
                            onClick={props.onAddRow}
                            variant='text-primary'
                            size='xlarge'
                            noPadding={true}
                        >
                            + Add a discount, fee, or tax
                        </Button>
                    </div>
                </div>
                <div className={styles['total-container']}>
                    <Body isBold={true} size='large'>
                        Total
                    </Body>
                    <Body isBold={true} size='large'>
                        {total}
                    </Body>
                </div>
            </div>
        </>
    );
}
